import React, { useEffect, useState, useRef, ChangeEvent } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import {
  AgentWorkflowRunStatus,
  useWorkflowRunStore,
} from "@/store/workflowRunStore";
import { Button } from "@/components/ui/button";
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Paperclip,
  X,
  Store,
  Mail,
  Timer,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { WorkflowRunViewWithOperations } from "./components/WorkflowRunViewWithOperations";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { formatRelative } from "date-fns";
import { fetchApi } from "@/lib/api";
import { Asset } from "@/store/quoteStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { useEmailSyncsStore } from "@/store/emailSyncsStore";
import { useConnectionsStore } from "@/store/connectionsStore";
import Layout from "@/components/layout";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SendButton } from "@/components/SendButton";

const MICROSOFT_OUTLOOK_APP_ID = "microsoft_outlook";

const getStatusIcon = (status: AgentWorkflowRunStatus) => {
  switch (status) {
    case "SUCCEEDED":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "FAILED":
      return <XCircle className="h-4 w-4 text-red-500" />;
    case "RUNNING":
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    case "QUEUED":
      return <Loader2 className="h-4 w-4 text-gray-500 animate-spin" />;
    case "REQUIRES_REVIEW":
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case "WAITING":
      return <Timer className="h-4 w-4 text-orange-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

export function AgentDetails() {
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    getAgent,
    getAgentById,
    getAgentState,
    getWorkflowVersions,
    getActiveWorkflowVersion,
    getActiveVersion,
  } = useAgentWorkflowStore();
  const {
    sendAgentChat,
    fetchWorkflowRunsByAgent,
    getRunsByAgentId,
    isLoadingList: runsLoading,
  } = useWorkflowRunStore();

  const organization = useOrganizationStore((state) => state.organization);
  const currentAgent = agentId ? getAgentById(agentId) : undefined;
  const agentState = agentId ? getAgentState(agentId) : null;
  const agentLoading = agentState?.isLoadingGet || false;
  const agentError = agentState?.getError;
  const activeVersion = agentId ? getActiveVersion(agentId) : undefined;
  const primaryAgentWorkflowId = organization?.primaryAgentWorkflowId;
  const isPrimaryAgent =
    agentId !== undefined && primaryAgentWorkflowId === agentId;

  const runs = agentId ? getRunsByAgentId(agentId) : [];
  const selectedRunId = searchParams.get("runId");
  const runViewOpen = selectedRunId !== null;
  const [newMessage, setNewMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [attachedAssets, setAttachedAssets] = useState<Asset[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quickQuestions = [
    "What quotes are waiting on follow up?",
    "Which opportunities are at risk this week?",
    "Do I have any high priority approvals right now?",
    "Are any deals stalled?",
  ];

  const { fetchEmailSyncs, getAllEmailSyncs } = useEmailSyncsStore();
  const { fetchConnectionsByApp, getConnectionsByApp } = useConnectionsStore();

  const emailSyncs = getAllEmailSyncs();
  const outlookConnections = getConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);

  const hasOutlookConnection =
    emailSyncs.length > 0 &&
    emailSyncs.some((sync) =>
      outlookConnections.some((conn) => conn.id === sync.connectionId),
    );

  // Check if the workflow has a receive_message_trigger
  const chatSupported = activeVersion?.allowsChat === true;

  useEffect(() => {
    if (agentId) {
      getAgent(agentId);
      getWorkflowVersions(agentId);
      getActiveWorkflowVersion(agentId);
    }
  }, [agentId, getAgent, getWorkflowVersions, getActiveWorkflowVersion]);

  useEffect(() => {
    if (agentId) {
      fetchWorkflowRunsByAgent(agentId);
    }
  }, [agentId, fetchWorkflowRunsByAgent]);

  useEffect(() => {
    fetchEmailSyncs();
    fetchConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);
  }, [fetchEmailSyncs, fetchConnectionsByApp]);

  const uploadFiles = async (files: FileList) => {
    if (files.length === 0) {
      return;
    }

    setIsUploadingFile(true);

    for (const file of Array.from(files)) {
      try {
        // Get presigned upload URL
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          continue;
        }

        const data = response.data as { uploadUrl: string; asset: Asset };

        // Upload file to S3
        await fetch(data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        // Add asset to attached assets
        setAttachedAssets((prev) => [...prev, data.asset]);
      } catch (_error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setIsUploadingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) {
      return;
    }
    await uploadFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDraggingOver) {
      setIsDraggingOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadFiles(files);
    }
  };

  const handleRemoveAsset = (assetId: string) => {
    setAttachedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  const handleOutlookIntegrationClick = () => {
    setIsIntegrationsOpen(false);
    navigate("/settings?tab=email-integrations");
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !agentId || isSendingMessage || isUploadingFile) {
      return;
    }

    setIsSendingMessage(true);
    try {
      const assetIds =
        attachedAssets.length > 0 ? attachedAssets.map((a) => a.id) : undefined;
      const workflowRun = await sendAgentChat(
        agentId,
        null,
        newMessage.trim(),
        assetIds,
      );
      if (workflowRun) {
        setNewMessage("");
        setAttachedAssets([]);
        // Open the workflow run view
        setSearchParams({ runId: workflowRun.id });
        // Refresh the runs list
        await fetchWorkflowRunsByAgent(agentId);
      } else {
        toast.error("Failed to start conversation");
      }
    } catch (_error) {
      toast.error("Error starting conversation");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleRunClick = (runId: string) => {
    setSearchParams({ runId });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return "-";
    }
    return formatRelative(new Date(dateString), new Date());
  };

  if (agentLoading) {
    return (
      <Layout breadcrumbs={[{ label: "", current: true }]}>
        <div className="flex-1 p-6">
          <div className="mb-6">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (agentError || !currentAgent) {
    return (
      <Layout breadcrumbs={[{ label: "", current: true }]}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              {agentError || "Agent not found"}
            </p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: currentAgent.name, current: true }]}>
      <div className="flex-1 bg-white">
        {chatSupported && activeVersion?.status === "ACTIVE" ? (
          <>
            <div className="min-h-[calc(100vh-128px)] px-6 py-6 flex flex-col items-center justify-center">
              <div className="w-full max-w-[768px]">
                <div className="text-center text-3xl sm:text-4xl font-semibold text-[#3d3a35]">
                  How can I help you today?
                </div>
                <Card
                  className={`mt-6 rounded-lg border transition-colors ${
                    isDraggingOver
                      ? "border-primary border-2 bg-primary/5"
                      : "border-border bg-white"
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <CardContent className="p-4 relative">
                    {isDraggingOver && (
                      <div className="absolute inset-0 flex items-center justify-center bg-primary/10 rounded-lg pointer-events-none z-10">
                        <div className="text-primary font-medium">
                          Drop files here
                        </div>
                      </div>
                    )}
                    {attachedAssets.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {attachedAssets.map((asset) => (
                          <Badge
                            key={asset.id}
                            variant="secondary"
                            className="gap-2"
                          >
                            <span className="truncate max-w-[220px]">
                              {asset.fileName}
                            </span>
                            <button
                              onClick={() => handleRemoveAsset(asset.id)}
                              className="hover:text-destructive"
                              disabled={isSendingMessage}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="mt-2">
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onSend={handleSendMessage}
                        placeholder={
                          isPrimaryAgent
                            ? "Ask a question about your opportunities, quotes, or customers..."
                            : "Type a task or question..."
                        }
                        disabled={isSendingMessage}
                        minRows={1}
                        className="border-0 bg-transparent text-base shadow-none focus-visible:ring-0 overflow-hidden"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingFile || isSendingMessage}
                            className="shrink-0 rounded-full border"
                          >
                            {isUploadingFile ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Paperclip className="h-4 w-4" />
                            )}
                          </Button>
                          <Popover
                            open={isIntegrationsOpen}
                            onOpenChange={setIsIntegrationsOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isSendingMessage}
                                className="shrink-0 rounded-full border"
                              >
                                <Store className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              side="top"
                              align="start"
                              className="w-80 p-0"
                            >
                              <div className="p-4">
                                <div className="flex items-center gap-2 mb-4">
                                  <h3 className="font-semibold text-sm">
                                    Work With
                                  </h3>
                                  <button
                                    className="ml-auto text-muted-foreground hover:text-foreground"
                                    onClick={() => setIsIntegrationsOpen(false)}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  <button
                                    onClick={handleOutlookIntegrationClick}
                                    disabled={hasOutlookConnection}
                                    className="w-full flex items-center gap-2 p-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent"
                                  >
                                    <div className="flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-background">
                                      <Mail className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 text-left">
                                      <div className="font-medium text-sm">
                                        Microsoft Outlook
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {hasOutlookConnection
                                          ? "Email connected"
                                          : "Connect your email"}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                      {hasOutlookConnection
                                        ? "Connected"
                                        : "Set up"}
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <SendButton
                          onClick={handleSendMessage}
                          disabled={
                            !newMessage.trim() ||
                            isSendingMessage ||
                            isUploadingFile
                          }
                          loading={isSendingMessage}
                          className="shrink-0 rounded-2"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {isPrimaryAgent && (
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {quickQuestions.map((question) => (
                      <Button
                        key={question}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full border-border text-xs"
                        onClick={() => setNewMessage(question)}
                        disabled={isSendingMessage}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 pb-6 text-center text-xs text-muted-foreground">
              Scroll down to see historical conversations.
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center my-6">
            <Card className="w-full max-w-2xl">
              <CardContent className="pt-6 text-center text-muted-foreground">
                Chat is not enabled for this workflow.
              </CardContent>
            </Card>
          </div>
        )}

        <div className="px-6 pb-12">
          <div className="mx-auto w-full max-w-4xl space-y-6">
            <div className="flex items-center gap-4">
              {!(agentLoading || runsLoading) &&
                currentAgent &&
                (currentAgent.status !== "ACTIVE" || !activeVersion) && (
                  <Badge
                    variant={
                      currentAgent.status === "ACTIVE" ? "default" : "secondary"
                    }
                  >
                    Disabled
                  </Badge>
                )}
            </div>

            <div>
              {runsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : runs.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">
                      No workflow runs yet
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => (
                    <Card
                      key={run.id}
                      className="cursor-pointer border-border hover:border-black/50 ease-in-out transition-all duration-200"
                      onClick={() => handleRunClick(run.id)}
                    >
                      <CardContent className="flex items-center justify-between gap-4 p-4">
                        <div>
                          <div className="text-sm">
                            {run.name ? (
                              run.name
                            ) : (
                              <Skeleton className="w-20 h-4" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Last message {formatDate(run.updatedAt)}
                          </div>
                        </div>
                        {getStatusIcon(run.status)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <WorkflowRunViewWithOperations
          runId={selectedRunId}
          isOpen={runViewOpen}
          onClose={() => {
            setSearchParams({});
          }}
        />
      </div>
    </Layout>
  );
}
